/**
 * Ordering of controller tiles on the dashboard, the scale comparison and the
 * settings lists (#686).
 *
 * A user's own controllers carry `sortOrder` from `controller.sort_order`;
 * watched ones carry it from their own `controller_watches.sort_order` row, so
 * each viewer orders shared stations for themselves.
 */
type Sortable = { id: number; sortOrder: number };

/** Watched controllers are stamped readOnly by the reducer, never by the API. */
type Sectioned = Sortable & { readOnly?: boolean };

export type MoveDirection = "up" | "down";

export type Section = "owned" | "watched";

/**
 * Which block of the dashboard a controller is renumbered within.
 *
 * Owned stations order by controller.sort_order, shared ones by the viewer's
 * own watch row, and they are listed as two separate blocks — so a move never
 * crosses from one to the other.
 */
export const sectionOf = (controller: Sectioned): Section =>
  controller.readOnly ? "watched" : "owned";

/** Whether the up/down control should be offered for this controller. */
export const canMove = <T extends Sectioned>(
  controllers: T[],
  controller: T,
  direction: MoveDirection,
): boolean => {
  const section = sortByOrder(
    controllers.filter((c) => sectionOf(c) === sectionOf(controller)),
  );
  const index = section.findIndex((c) => c.id === controller.id);
  if (index === -1) return false;
  return direction === "up" ? index > 0 : index < section.length - 1;
};

// The id tiebreak is load-bearing: the migration defaults every row to 0 and
// the API returns controllers ORDER BY idcontroller, so until a user actually
// reorders something this reproduces exactly the order they see today.
const byOrderThenId = (a: Sortable, b: Sortable) =>
  a.sortOrder - b.sortOrder || a.id - b.id;

export const sortByOrder = <T extends Sortable>(controllers: T[]): T[] =>
  [...controllers].sort(byOrderThenId);

/**
 * The admin/manager overview lists controllers across all users, where one
 * owner's ordering means nothing next to another's — fall back to id.
 */
export const sortById = <T extends Sortable>(controllers: T[]): T[] =>
  [...controllers].sort((a, b) => a.id - b.id);

/**
 * Swap a controller with its neighbour and renumber the whole list densely.
 *
 * Renumbering rather than swapping two values is what makes the first move
 * work at all: every row starts at 0, so swapping would leave the ties intact
 * and the id tiebreak would immediately undo the move.
 *
 * Returns the input array unchanged (same reference) when the move would leave
 * the list, so callers can skip both the optimistic update and the request.
 */
export const moveInList = <T extends Sortable>(
  controllers: T[],
  id: number,
  direction: MoveDirection,
): T[] => {
  const ordered = sortByOrder(controllers);
  const from = ordered.findIndex((controller) => controller.id === id);
  if (from === -1) return controllers;

  const to = direction === "up" ? from - 1 : from + 1;
  if (to < 0 || to >= ordered.length) return controllers;

  [ordered[from], ordered[to]] = [ordered[to], ordered[from]];
  return ordered.map((controller, index) => ({
    ...controller,
    sortOrder: index,
  }));
};
